import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  addPartenaire,
  deletePartenaire,
  getAllPartenaires,
  updatePartenaire,
  uploadImage,
} from "../../../../../services/partenairesServices";

import {
  buildPartenairesStats,
  filterAndSortPartenaires,
  isDemoUser,
  normalizePartenairePayload,
  safeJsonParse,
} from "../utils/partenairesUtils";

const INITIAL_FILTERS = {
  query: "",
  status: "all",
  sortOrder: "ordre_asc",
};

export default function usePartenaires() {
  const [partenaires, setPartenaires] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedPartenaire, setSelectedPartenaire] = useState(null);

  const [deleteCandidateId, setDeleteCandidateId] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const setFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const loadUserMode = useCallback(() => {
    const user =
      safeJsonParse(localStorage.getItem("user"), null) ||
      safeJsonParse(localStorage.getItem("userInfo"), null);

    setIsDemoMode(isDemoUser(user));
  }, []);

  const refreshPartenaires = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getAllPartenaires();
      setPartenaires(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des partenaires :", error);
      toast.error("Erreur lors de la récupération des partenaires.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserMode();
    refreshPartenaires();
  }, [loadUserMode, refreshPartenaires]);

  const guardDemoMode = useCallback(() => {
    if (!isDemoMode) return false;

    toast.info("Fonction désactivée en mode démonstration.");
    return true;
  }, [isDemoMode]);

  const uploadPartenaireImage = useCallback(async (imageFile) => {
    if (!imageFile) return null;

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("category", "partenaires");

    const uploadResponse = await uploadImage(formData);

    return (
      uploadResponse?.filePath ||
      uploadResponse?.imageUrl ||
      uploadResponse?.url ||
      null
    );
  }, []);

  const buildPayloadWithImage = useCallback(
    async (formData) => {
      const payload = normalizePartenairePayload(formData);

      if (formData.imageFile) {
        const uploadedImageUrl = await uploadPartenaireImage(
          formData.imageFile
        );

        if (uploadedImageUrl) {
          payload.photo_url = uploadedImageUrl;
        }
      }

      delete payload.imageFile;

      return payload;
    },
    [uploadPartenaireImage]
  );

  const filteredPartenaires = useMemo(
    () => filterAndSortPartenaires(partenaires, filters),
    [partenaires, filters]
  );

  const stats = useMemo(
    () => buildPartenairesStats(partenaires, filteredPartenaires),
    [partenaires, filteredPartenaires]
  );

  const openCreateDrawer = useCallback(() => {
    if (guardDemoMode()) return;

    setSelectedPartenaire(null);
    setDrawerMode("create");
  }, [guardDemoMode]);

  const openEditDrawer = useCallback(
    (partenaire) => {
      if (guardDemoMode()) return;

      setSelectedPartenaire(partenaire);
      setDrawerMode("edit");
    },
    [guardDemoMode]
  );

  const openDetailsDrawer = useCallback((partenaire) => {
    setSelectedPartenaire(partenaire);
    setDrawerMode("details");
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerMode(null);
    setSelectedPartenaire(null);
  }, []);

  const createPartenaire = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = await buildPayloadWithImage(formData);

        await addPartenaire(payload);

        toast.success("Partenaire créé avec succès.");
        closeDrawer();
        await refreshPartenaires();
      } catch (error) {
        console.error("Erreur lors de la création du partenaire :", error);
        toast.error("Erreur lors de la création du partenaire.");
      } finally {
        setSaving(false);
      }
    },
    [buildPayloadWithImage, closeDrawer, guardDemoMode, refreshPartenaires]
  );

  const savePartenaire = useCallback(
    async (formData) => {
      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = await buildPayloadWithImage(formData);

        await updatePartenaire(payload);

        toast.success("Partenaire modifié avec succès.");
        closeDrawer();
        await refreshPartenaires();
      } catch (error) {
        console.error("Erreur lors de la modification du partenaire :", error);
        toast.error("Erreur lors de la modification du partenaire.");
      } finally {
        setSaving(false);
      }
    },
    [buildPayloadWithImage, closeDrawer, guardDemoMode, refreshPartenaires]
  );

  const askDelete = useCallback(
    (partenaireId) => {
      if (guardDemoMode()) return;

      setDeleteCandidateId(partenaireId);
    },
    [guardDemoMode]
  );

  const cancelDelete = useCallback(() => {
    setDeleteCandidateId(null);
  }, []);

  const confirmDelete = useCallback(
    async (partenaireId) => {
      if (guardDemoMode()) return;

      try {
        await deletePartenaire(partenaireId);

        toast.success("Partenaire supprimé avec succès.");
        setDeleteCandidateId(null);
        await refreshPartenaires();
      } catch (error) {
        console.error("Erreur lors de la suppression du partenaire :", error);
        toast.error("Erreur lors de la suppression du partenaire.");
      }
    },
    [guardDemoMode, refreshPartenaires]
  );

  return {
    loading,
    saving,
    filters,
    setFilter,
    partenaires,
    filteredPartenaires,
    selectedPartenaire,
    drawerMode,
    deleteCandidateId,
    stats,
    actions: {
      refreshPartenaires,
      openCreateDrawer,
      openEditDrawer,
      openDetailsDrawer,
      closeDrawer,
      createPartenaire,
      updatePartenaire: savePartenaire,
      askDelete,
      cancelDelete,
      confirmDelete,
    },
  };
}